import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { useRouter } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Brand, Neutrals, primaryAlpha } from '@/constants/theme';
import { useCreateHousehold } from '@/hooks/useHousehold';
import { trackEvent } from '@/lib/analytics';

export default function CreateHouseholdScreen() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { createHousehold, loading, error } = useCreateHousehold();
  const [name, setName] = useState('');

  const handleCreate = async () => {
    try {
      const household = await createHousehold(name.trim() || 'My Household');
      if (household) {
        trackEvent('Household created', { household_id: household.id });
        router.replace('/(tabs)/settings');
      }
    } catch {
      // Error already set by hook
    }
  };

  return (
    <View style={styles.container}>
      <LinearGradient
        colors={[...Brand.primaryGradient]}
        style={styles.background}
      />
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <ScrollView
          contentContainerStyle={[
            styles.scrollContent,
            { paddingTop: Math.max(insets.top, 16) },
          ]}
          keyboardShouldPersistTaps="handled"
        >
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => router.back()}
            accessibilityLabel="Go back"
          >
            <Ionicons name="arrow-back" size={20} color={Brand.primary} />
            <Text style={styles.backText}>Back</Text>
          </TouchableOpacity>

          <View style={styles.header}>
            <View style={styles.iconContainer}>
              <Ionicons name="people" size={48} color={Brand.primary} />
            </View>
            <Text style={styles.title}>Create household</Text>
            <Text style={styles.subtitle}>
              Set up a shared household to manage grocery lists and meals together.
            </Text>
          </View>

          <View style={styles.card}>
            <Text style={styles.label}>Household name</Text>
            <Input
              value={name}
              onChangeText={setName}
              placeholder="e.g., Smith Family"
              style={styles.input}
              editable={!loading}
            />
            {error && (
              <Text style={styles.errorText}>{error.message}</Text>
            )}
            <Button
              onPress={handleCreate}
              disabled={loading}
              style={styles.submitButton}
            >
              {loading ? (
                <ActivityIndicator size="small" color={Neutrals.white} />
              ) : (
                'Create household'
              )}
            </Button>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  background: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  },
  keyboardView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 100,
    maxWidth: 864,
    alignSelf: 'center',
    width: '100%',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    marginBottom: 24,
    paddingVertical: 4,
  },
  backText: {
    color: Brand.primary,
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 20,
  },
  header: {
    marginBottom: 32,
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: primaryAlpha(0.2),
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: Neutrals.textPrimary,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: Neutrals.textSecondary,
    lineHeight: 24,
  },
  card: {
    backgroundColor: Neutrals.whiteFaded90,
    borderRadius: 20,
    padding: 24,
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.06)',
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: Neutrals.textPrimary,
    marginBottom: 8,
  },
  input: {
    marginBottom: 16,
  },
  errorText: {
    fontSize: 14,
    color: Neutrals.destructiveMuted,
    marginBottom: 16,
  },
  submitButton: {
    marginTop: 8,
  },
});
